/**
 * Template Dialog Component
 * 
 * Modal dialog for creating and editing templates.
 * Extended from PromptDialog with description, category, and profile binding fields.
 * Per design.md: Premium UI, semantic tokens, lucide icons.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { Doc } from 'convex/_generated/dataModel';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogClose,
    Button,
    Input,
    Textarea,
    Badge,
    Loader2,
    Select,
} from '@uprosal/ui';

interface TemplateDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    template?: Doc<'prompts'> | null;
    onSuccess?: () => void;
}

// Template categories
const CATEGORIES = [
    { value: '', label: 'No Category' },
    { value: 'General', label: 'General' },
    { value: 'Enthusiasm', label: 'Enthusiasm' },
    { value: 'Questions', label: 'Questions' },
    { value: 'Proof', label: 'Proof' },
    { value: 'Experience', label: 'Experience' },
    { value: 'Confidence', label: 'Confidence' },
    { value: 'Technical', label: 'Technical' },
    { value: 'Speed', label: 'Speed' },
    { value: 'Expert', label: 'Expert' },
    { value: 'Process', label: 'Process' },
    { value: 'Quick', label: 'Quick' },
    { value: 'Skills', label: 'Skills' },
    { value: 'Invites', label: 'Invites' },
    { value: 'Employment', label: 'Employment' },
    { value: 'New Freelancer', label: 'New Freelancer' },
    { value: 'Vision', label: 'Vision' },
    { value: 'Professional', label: 'Professional' },
];

// Friendly error messages for users
const FRIENDLY_ERRORS: Record<string, string> = {
    'Template must be at least 10 characters': 'Template is too short. Please write at least 10 characters.',
    'Name is required': 'Please enter a name for your template.',
    'Template is required': 'Please enter a template.',
};

function getFriendlyError(error: unknown): string {
    if (error instanceof Error) {
        for (const [pattern, friendly] of Object.entries(FRIENDLY_ERRORS)) {
            if (error.message.includes(pattern)) {
                return friendly;
            }
        }
        return 'Something went wrong. Please try again.';
    }
    return 'Something went wrong. Please try again.';
}

/**
 * Extract variables from template (e.g., {{name}}, {{jobTitle}} or {instruction})
 */
function extractVariables(template: string): string[] {
    // Match both {{variable}} and {instruction} patterns
    const doubleBrace = /\{\{(\w+)\}\}/g;
    const singleBrace = /\{([^{}]+)\}/g;

    const matches = new Set<string>();
    let match;

    while ((match = doubleBrace.exec(template)) !== null) {
        matches.add(`{{${match[1]}}}`);
    }

    // Count single-brace instructions (AI prompts)
    let instructionCount = 0;
    while ((match = singleBrace.exec(template)) !== null) {
        if (!match[0].includes('{{')) {
            instructionCount++;
        }
    }

    const result = Array.from(matches);
    if (instructionCount > 0) {
        result.push(`${instructionCount} AI instruction(s)`);
    }

    return result;
}

export function TemplateDialog({ open, onOpenChange, template, onSuccess }: TemplateDialogProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [templateContent, setTemplateContent] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Fetch profiles for binding
    const profiles = useQuery(api.queries.profiles.listConnected, {}) as Doc<'connectedProfiles'>[] | undefined;
    const [boundProfileId, setBoundProfileId] = useState<string>('');

    const createTemplate = useMutation(api.mutations.prompts.create);
    const updateTemplate = useMutation(api.mutations.prompts.update);

    const isEditing = !!template;
    const variables = extractVariables(templateContent);

    // Reset form when dialog opens/closes or template changes
    useEffect(() => {
        if (open && template) {
            setName(template.name);
            setDescription(template.description || '');
            setCategory(template.category || '');
            setTemplateContent(template.template);
            setBoundProfileId(template.boundProfileId || '');
        } else if (open && !template) {
            setName('');
            setDescription('');
            setCategory('');
            setTemplateContent('');
            setBoundProfileId('');
        }
        setError(null);
    }, [open, template]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Client-side validation
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();
        const trimmedTemplate = templateContent.trim();

        if (!trimmedName) {
            setError('Please enter a name for your template.');
            return;
        }
        if (trimmedName.length > 100) {
            setError('Name is too long. Maximum 100 characters.');
            return;
        }
        if (!trimmedTemplate) {
            setError('Please enter a template.');
            return;
        }
        if (trimmedTemplate.length < 10) {
            setError('Template is too short. Please write at least 10 characters.');
            return;
        }
        if (trimmedTemplate.length > 10000) {
            setError('Template is too long. Maximum 10,000 characters.');
            return;
        }

        setIsSaving(true);

        try {
            if (isEditing && template) {
                await updateTemplate({
                    promptId: template._id,
                    patch: {
                        name: trimmedName,
                        description: trimmedDescription || undefined,
                        template: trimmedTemplate,
                        category: category || undefined,
                        boundProfileId: boundProfileId ? boundProfileId as any : undefined,
                    },
                });
            } else {
                await createTemplate({
                    name: trimmedName,
                    description: trimmedDescription || undefined,
                    template: trimmedTemplate,
                    category: category || undefined,
                    boundProfileId: boundProfileId ? boundProfileId as any : undefined,
                });
            }

            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            console.error('Failed to save template:', err);
            setError(getFriendlyError(err));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[600px] max-w-[90vw] max-h-[90vh] overflow-y-auto">
                <DialogClose onClick={() => onOpenChange(false)} />

                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? 'Edit Template' : 'Create New Template'}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? 'Update your proposal template and settings.'
                            : 'Create a reusable template for your proposals. Use {instructions} for AI content generation.'
                        }
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {/* Name field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="template-name"
                                className="text-sm font-medium text-foreground"
                            >
                                Name
                            </label>
                            <Input
                                id="template-name"
                                placeholder="e.g., Quick Response, Technical Proposal"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={isSaving}
                                maxLength={100}
                            />
                        </div>

                        {/* Description field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="template-description"
                                className="text-sm font-medium text-foreground"
                            >
                                Description
                                <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                            </label>
                            <Input
                                id="template-description"
                                placeholder="Brief description of when to use this template"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                disabled={isSaving}
                                maxLength={500}
                            />
                        </div>

                        {/* Category & Profile row */}
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-1.5">
                                <label
                                    htmlFor="template-category"
                                    className="block text-sm font-medium text-foreground"
                                >
                                    Category
                                </label>
                                <Select
                                    id="template-category"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    disabled={isSaving}
                                    className="w-full"
                                >
                                    {CATEGORIES.map((cat) => (
                                        <option key={cat.value} value={cat.value}>
                                            {cat.label}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <label
                                    htmlFor="template-profile"
                                    className="block text-sm font-medium text-foreground"
                                >
                                    Bound Profile <span className="text-muted-foreground font-normal">(optional)</span>
                                </label>
                                <Select
                                    id="template-profile"
                                    value={boundProfileId}
                                    onChange={(e) => setBoundProfileId(e.target.value)}
                                    disabled={isSaving}
                                    className="w-full"
                                >
                                    <option value="">Any Profile</option>
                                    {profiles?.map((profile) => (
                                        <option key={profile._id} value={profile._id}>
                                            {profile.profileLabel}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        {/* Template content field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="template-content"
                                className="text-sm font-medium text-foreground"
                            >
                                Template
                            </label>
                            <Textarea
                                id="template-content"
                                placeholder="{Write a compelling opener for this job based on my skills}

Here is how I would approach your project:

{Create a list of 2-4 steps based on the job requirements}

Please message me on Upwork to discuss further.

{Write out my name and profession}"
                                value={templateContent}
                                onChange={(e) => setTemplateContent(e.target.value)}
                                disabled={isSaving}
                                className="min-h-[200px] font-mono text-sm"
                            />
                            <p className="text-xs text-muted-foreground">
                                {templateContent.length}/10,000 characters
                            </p>
                        </div>

                        {/* Variables/Instructions preview */}
                        {variables.length > 0 && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">
                                    Detected Elements
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {variables.map((v, i) => (
                                        <Badge key={i} variant="secondary">
                                            {v}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Error message */}
                        {error && (
                            <p className="text-sm text-destructive">{error}</p>
                        )}
                    </div>

                    <DialogFooter className="mt-6 gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isSaving}>
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : isEditing ? (
                                'Save Changes'
                            ) : (
                                'Create Template'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
